import { Injectable } from '@angular/core';

/**
 * Supported languages for static error messages.
 * Matches the 6 language slots used by the BE (see i18n-inserts.sql):
 * 1=de, 2=en, 3=it, 4=fr, 6=es, 9=nl.
 */
export type ErrorLang = 'de' | 'en' | 'it' | 'fr' | 'es' | 'nl';

/**
 * Static (client-side) translations used when the app cannot load the
 * regular BE translation payload — typically because the very request
 * that would deliver translations has failed (e.g. HTTP 410 expired).
 *
 * Extend this map when a new static error text is needed.
 */
const EXPIRED_MESSAGES: Record<ErrorLang, string> = {
	de: 'Dieser Link ist nicht mehr gültig. Feedback-Links laufen nach einer gewissen Zeit ab.',
	en: 'This link is no longer valid. Feedback links expire after a certain time.',
	it: 'Questo link non è più valido. I link di feedback scadono dopo un certo tempo.',
	fr: 'Ce lien n\'est plus valide. Les liens de feedback expirent après un certain temps.',
	es: 'Este enlace ya no es válido. Los enlaces de feedback caducan después de un tiempo.',
	nl: 'Deze link is niet meer geldig. Feedback-links verlopen na een bepaalde tijd.',
};

/**
 * Patterns identifying the BE language by its localized suffix in the
 * expired-token message "UID {token} {suffix}" (see HELP-551592).
 * Order only matters if two patterns could overlap; currently they don't.
 * Add a new entry when the BE adds a new language literal.
 */
const EXPIRED_LANG_PATTERNS: Array<{ lang: ErrorLang; pattern: RegExp }> = [
	{ lang: 'en', pattern: /has expired/i },
	{ lang: 'it', pattern: /è scaduta/i },
	{ lang: 'fr', pattern: /a expiré/i },
	{ lang: 'es', pattern: /ha expirado/i },
	{ lang: 'nl', pattern: /is verlopen/i },
	{ lang: 'de', pattern: /ist abgelaufen/i },
];

const DEFAULT_LANG: ErrorLang = 'en';

/**
 * Provides localized messages for error cases where the normal i18n
 * payload from the BE is unavailable (request itself failed).
 *
 * Language is inferred from the BE error body first (most reliable
 * because the BE knows the customer's language), then from the browser,
 * finally falling back to English.
 */
@Injectable({
	providedIn: 'root',
})
export class ErrorMessageService {
	public getExpiredMessage(backendMessage: string | null): string {
		const lang = this.detectLanguage(backendMessage) || this.browserLanguage() || DEFAULT_LANG;
		return EXPIRED_MESSAGES[lang] ?? EXPIRED_MESSAGES[DEFAULT_LANG];
	}

	public detectLanguage(backendMessage: string | null): ErrorLang | null {
		if (!backendMessage) return null;
		const match = EXPIRED_LANG_PATTERNS.find((p) => p.pattern.test(backendMessage));
		return match ? match.lang : null;
	}

	private browserLanguage(): ErrorLang | null {
		const raw = (navigator.language || '').slice(0, 2).toLowerCase();
		return this.isSupportedLang(raw) ? raw : null;
	}

	private isSupportedLang(value: string): value is ErrorLang {
		return value in EXPIRED_MESSAGES;
	}
}
